/**
 * VendingOnboardingTour — v2.5.901
 *
 * Full-fat vending walkthrough, ported from the general SampleDataTour so both
 * flows behave identically:
 *   • Fetches steps from /api/tour/steps/vending (with a client-side fallback
 *     for stale backends).
 *   • Renders a translucent overlay + spotlight cutout around the current
 *     step's target element (SVG mask).
 *   • Positions the tooltip relative to the target (top / bottom / left /
 *     right / center), clamped inside the viewport.
 *   • Auto-scrolls the target into view.
 *   • Two-tier narration pipeline:
 *       1. Server MP3 at /api/tour/audio/{step_id}
 *       2. Browser Web Speech API fallback (reads narration text aloud)
 *   • Autoplay on step change (user gesture from Replay button unlocks it).
 *   • Auto-advance to next step when narration ends.
 *   • Mute + Play/Pause + Skip controls in the header.
 *
 * The component is mounted at AppLayout level for every /vending/* route, so
 * subpages inherit it. Listens for the `vending-tour-replay` custom event
 * dispatched by Sidebar / Vending Fleet header / Settings → Help & Tours.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX, Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { VENDING_TOUR_STEPS_FALLBACK, VENDING_TOUR_FALLBACK_VERSION } from '../data/vendingTourStepsFallback';

const API = process.env.REACT_APP_BACKEND_URL;
const MUTE_KEY = 'vending_tour_muted';

function buildFallbackSteps() {
  // One-time console breadcrumb so a stale Plesk bundle is trivial to spot
  // in DevTools — grep for "VendingTour fallback" and confirm the version.
  try {
    if (typeof window !== 'undefined' && !window.__VENDING_TOUR_FALLBACK_LOGGED) {
      window.__VENDING_TOUR_FALLBACK_LOGGED = true;
      console.info(
        `[VendingTour fallback ${VENDING_TOUR_FALLBACK_VERSION}] loaded ${VENDING_TOUR_STEPS_FALLBACK.length} steps`
      );
    }
  } catch { /* noop */ }
  return VENDING_TOUR_STEPS_FALLBACK.map(s => ({
    id: s.id,
    title: s.title,
    target: s.target,
    page: s.page,
    position: s.position || 'bottom',
    narration: s.narration,
    body: s.narration, // legacy alias
    has_audio: false,
    completed: false,
  }));
}

function readMutePref() {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

export default function VendingOnboardingTour() {
  const [steps, setSteps] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioPaused, setAudioPaused] = useState(false);
  const [audioMuted, setAudioMuted] = useState(readMutePref);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const audioRef = useRef(null);
  const ttsUtteranceRef = useRef(null);
  const goNextRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // ─── Audio pipeline ─────────────────────────────────────────────────────
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch { /* older Safari */ }
      audioRef.current = null;
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try { window.speechSynthesis.cancel(); } catch { /* not supported */ }
    }
    ttsUtteranceRef.current = null;
    setAudioPlaying(false);
    setAudioPaused(false);
  }, []);

  const speakWithTTS = useCallback((text, onEnd) => {
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return false;
    try {
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(text);
      utter.rate = 0.98; utter.pitch = 1.0; utter.volume = 1.0;
      utter.onend = () => {
        setAudioPlaying(false); setAudioPaused(false);
        if (onEnd) onEnd();
      };
      utter.onerror = () => { setAudioPlaying(false); setAudioPaused(false); };
      // Prefer an English voice when available
      const pickVoice = () => {
        const voices = window.speechSynthesis.getVoices() || [];
        const en = voices.find(v => /^en/i.test(v.lang)) || voices[0];
        if (en) utter.voice = en;
      };
      pickVoice();
      if (!utter.voice) window.speechSynthesis.onvoiceschanged = pickVoice;
      ttsUtteranceRef.current = utter;
      window.speechSynthesis.speak(utter);
      setAudioPlaying(true); setAudioPaused(false);
      return true;
    } catch { return false; }
  }, []);

  const playStepAudio = useCallback((currentStep) => {
    if (!currentStep || audioMuted) return;
    stopAudio();
    const onEnd = () => {
      // Auto-advance to next step when narration finishes.
      setTimeout(() => { if (goNextRef.current) goNextRef.current(); }, 700);
    };
    if (currentStep.has_audio) {
      try {
        const el = new Audio(`${API}/api/tour/audio/${currentStep.id}`);
        audioRef.current = el;
        let fellBack = false;
        const fallback = () => {
          if (fellBack) return;
          fellBack = true;
          if (!speakWithTTS(currentStep.narration || currentStep.body, onEnd)) setAudioPlaying(false);
        };
        el.onended = () => { setAudioPlaying(false); setAudioPaused(false); onEnd(); };
        el.onerror = fallback;
        el.play().then(() => { setAudioPlaying(true); setAudioPaused(false); }).catch(fallback);
        return;
      } catch { /* fall through */ }
    }
    if (!speakWithTTS(currentStep.narration || currentStep.body, onEnd)) setAudioPlaying(false);
  }, [audioMuted, speakWithTTS, stopAudio]);

  // ─── Fetch tour steps (with client fallback for stale backends) ─────────
  // v2.5.912 — Only auto-open on the /vending root page. Sub-routes like
  // /vending/contracts, /vending/commissions must not have the tour hijack
  // their pointer events. Also persist a permanent "seen" flag so returning
  // sessions don't re-fire it (session-scoped dismiss was too aggressive).
  const AUTO_OPEN_ROUTES = ['/vending', '/vending/'];
  const isRootVending = () => {
    try {
      const p = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
      return AUTO_OPEN_ROUTES.includes(p);
    } catch { return false; }
  };
  const permanentlyDismissed = () => {
    try { return localStorage.getItem('vending_tour_completed_v1') === '1'; } catch { return false; }
  };
  const load = useCallback(async (force = false) => {
    // Only auto-fire when landing on /vending root. Force (replay button) always fires.
    if (!force && (!isRootVending() || permanentlyDismissed())) {
      setIsVisible(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/tour/steps/vending`);
      if (!data.has_access) {
        // v2.6.8 finality — if the caller is a super_admin/platform_owner
        // OR triggered a Replay, we still show the fallback so the tour is
        // reachable even when the backend gate says no. Non-privileged users
        // continue to be blocked exactly as before.
        const meRole = (() => {
          try {
            const raw = localStorage.getItem('user');
            return raw ? ((JSON.parse(raw).role) || '').toLowerCase() : '';
          } catch { return ''; }
        })();
        const isPrivileged = meRole === 'super_admin' || meRole === 'country_operator';
        if (force || isPrivileged) {
          setSteps(buildFallbackSteps());
          setHasAccess(true); setCurrentIdx(0);
          try { sessionStorage.removeItem('vending_tour_dismissed'); } catch { /* storage blocked */ }
          if (force || !sessionStorage.getItem('vending_tour_dismissed')) {
            setIsVisible(true);
          }
          return;
        }
        setHasAccess(false); return;
      }
      if (!force && data.completed) { setHasAccess(false); return; }
      // Backend steps may omit `narration` — hydrate from fallback so TTS still works.
      const backendSteps = (data.steps || []).map(s => {
        const fb = VENDING_TOUR_STEPS_FALLBACK.find(x => x.id === s.id);
        return {
          ...s,
          narration: s.narration || s.body || fb?.narration || '',
          position: s.position || fb?.position || 'bottom',
          page: s.page || fb?.page || '/vending',
        };
      });
      setSteps(backendSteps.length ? backendSteps : buildFallbackSteps());
      setHasAccess(true);
      setCurrentIdx(force ? 0 : (data.current_step || 0));
      if (force) {
        try { sessionStorage.removeItem('vending_tour_dismissed'); } catch { /* storage blocked */ }
        try { localStorage.removeItem('vending_tour_completed_v1'); } catch { /* storage blocked */ }
        setIsVisible(true);
      } else if (!sessionStorage.getItem('vending_tour_dismissed')) {
        setIsVisible(true);
      }
    } catch (e) {
      if (force) {
        setSteps(buildFallbackSteps());
        setHasAccess(true); setCurrentIdx(0);
        try { sessionStorage.removeItem('vending_tour_dismissed'); } catch { /* storage blocked */ }
        setIsVisible(true);
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Listen for external replay dispatches from sidebar / header / Help tab.
  useEffect(() => {
    const handler = () => load(true);
    window.addEventListener('vending-tour-replay', handler);
    return () => window.removeEventListener('vending-tour-replay', handler);
  }, [load]);

  const step = steps[currentIdx];

  // ─── Spotlight positioning ──────────────────────────────────────────────
  const updateSpotlight = useCallback(() => {
    if (!isVisible || !step) { setSpotlightRect(null); return; }
    if (step.position === 'center') { setSpotlightRect(null); return; }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) { setSpotlightRect(null); return; }
      setSpotlightRect({
        top: rect.top - 8, left: rect.left - 8,
        width: rect.width + 16, height: rect.height + 16,
      });
    } else {
      setSpotlightRect(null);
    }
  }, [isVisible, step]);

  const updateSpotlightAndScroll = useCallback(() => {
    if (!isVisible || !step) return;
    if (step.position === 'center') { setSpotlightRect(null); return; }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) { setSpotlightRect(null); return; }
      setSpotlightRect({
        top: rect.top - 8, left: rect.left - 8,
        width: rect.width + 16, height: rect.height + 16,
      });
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* older browsers */ }
    }
  }, [isVisible, step]);

  useEffect(() => {
    if (!isVisible) return;
    // Multiple retries in case the target element renders after route change.
    const timers = [
      setTimeout(updateSpotlightAndScroll, 250),
      setTimeout(updateSpotlightAndScroll, 700),
      setTimeout(updateSpotlightAndScroll, 1400),
    ];
    window.addEventListener('resize', updateSpotlight);
    window.addEventListener('scroll', updateSpotlight, true);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', updateSpotlight);
      window.removeEventListener('scroll', updateSpotlight, true);
    };
  }, [isVisible, currentIdx, updateSpotlight, updateSpotlightAndScroll, location.pathname]);

  // Navigate to step's page if user isn't already there.
  useEffect(() => {
    if (!isVisible || !step?.page) return;
    if (location.pathname !== step.page && !location.pathname.startsWith(step.page + '/')) {
      navigate(step.page);
    }
  }, [isVisible, step, navigate, location.pathname]);

  // Autoplay narration whenever the step changes (or overlay opens).
  useEffect(() => {
    if (!isVisible || !step || audioMuted) return;
    const t = setTimeout(() => playStepAudio(step), 450);
    return () => clearTimeout(t);
  }, [isVisible, currentIdx, audioMuted, playStepAudio, step]);

  // Cleanup audio on unmount / close.
  useEffect(() => () => stopAudio(), [stopAudio]);
  useEffect(() => { if (!isVisible) stopAudio(); }, [isVisible, stopAudio]);

  // ─── Step navigation ────────────────────────────────────────────────────
  const goNext = useCallback(async () => {
    if (!step) return;
    stopAudio();
    try {
      await axios.post(`${API}/api/tour/steps/vending/progress`, { step_id: step.id, completed: true });
    } catch { /* silent */ }
    if (currentIdx >= steps.length - 1) {
      try { await axios.post(`${API}/api/tour/steps/vending/complete`); } catch { /* silent */ }
      toast.success('Vending tour complete — replay it any time from the header, sidebar, or Settings');
      setIsVisible(false);
      return;
    }
    setCurrentIdx(i => i + 1);
  }, [currentIdx, step, steps.length, stopAudio]);

  // Keep goNext ref fresh so audio.onended can auto-advance without stale closures.
  useEffect(() => { goNextRef.current = goNext; });

  const goPrev = () => { stopAudio(); setCurrentIdx(i => Math.max(0, i - 1)); };

  const dismiss = () => {
    stopAudio();
    sessionStorage.setItem('vending_tour_dismissed', '1');
    // v2.5.912 — Also persist so it doesn't reopen on every future login.
    try { localStorage.setItem('vending_tour_completed_v1', '1'); } catch { /* storage blocked */ }
    setIsVisible(false);
  };

  const togglePauseAudio = () => {
    // TTS pause path
    if (ttsUtteranceRef.current && window.speechSynthesis) {
      if (window.speechSynthesis.paused) { try { window.speechSynthesis.resume(); setAudioPaused(false); setAudioPlaying(true); } catch { /* nop */ } }
      else if (window.speechSynthesis.speaking) { try { window.speechSynthesis.pause(); setAudioPaused(true); setAudioPlaying(false); } catch { /* nop */ } }
      else if (step) playStepAudio(step);
      return;
    }
    // HTMLAudio pause path
    const a = audioRef.current;
    if (!a) { if (step) playStepAudio(step); return; }
    if (a.paused) { a.play().then(() => { setAudioPaused(false); setAudioPlaying(true); }).catch(() => {}); }
    else { a.pause(); setAudioPaused(true); setAudioPlaying(false); }
  };

  const persistMuted = (v) => {
    setAudioMuted(v);
    try { localStorage.setItem(MUTE_KEY, v ? '1' : '0'); } catch { /* storage blocked */ }
    if (v) stopAudio();
    else if (step) playStepAudio(step);
  };

  // ─── Tooltip positioning ────────────────────────────────────────────────
  const getTooltipStyle = () => {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const margin = 16;
    const isMobile = vw < 640;
    const tipW = isMobile ? Math.min(vw - margin * 2, 420) : 420;
    const tipH = isMobile ? 340 : 300;

    if (isMobile) {
      return {
        top: Math.max(margin, vh - tipH - margin),
        left: margin, right: margin,
        width: `calc(100vw - ${margin * 2}px)`,
        maxWidth: '100%',
        maxHeight: `calc(100vh - ${margin * 2}px)`,
        overflowY: 'auto',
      };
    }
    if (!spotlightRect) {
      return {
        top: Math.max(margin, (vh - tipH) / 2),
        left: Math.max(margin, (vw - tipW) / 2),
        width: tipW, maxWidth: '420px',
        maxHeight: `calc(100vh - ${margin * 2}px)`,
        overflowY: 'auto',
      };
    }

    const pos = step?.position || 'bottom';
    let top; let left;
    if (pos === 'bottom') {
      top = spotlightRect.top + spotlightRect.height + margin;
      if (top + tipH + margin > vh) top = spotlightRect.top - tipH - margin;
      left = spotlightRect.left;
    } else if (pos === 'top') {
      top = spotlightRect.top - tipH - margin;
      if (top < margin) top = spotlightRect.top + spotlightRect.height + margin;
      left = spotlightRect.left;
    } else if (pos === 'right') {
      left = spotlightRect.left + spotlightRect.width + margin;
      if (left + tipW + margin > vw) left = spotlightRect.left - tipW - margin;
      top = spotlightRect.top;
    } else if (pos === 'left') {
      left = spotlightRect.left - tipW - margin;
      if (left < margin) left = spotlightRect.left + spotlightRect.width + margin;
      top = spotlightRect.top;
    } else {
      top = spotlightRect.top + spotlightRect.height + margin;
      left = spotlightRect.left;
    }
    top = Math.min(Math.max(margin, top), Math.max(margin, vh - tipH - margin));
    left = Math.min(Math.max(margin, left), Math.max(margin, vw - tipW - margin));
    return {
      top, left, width: tipW, maxWidth: '420px',
      maxHeight: `calc(100vh - ${margin * 2}px)`,
      overflowY: 'auto',
    };
  };

  if (!hasAccess || !isVisible || !step) return null;

  return (
    <>
      <div className="fixed inset-0 z-[9998]" style={{ pointerEvents: 'none' }} data-testid="vending-tour-overlay">
        {/* SVG mask — darkens everything but the spotlight rect */}
        <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'auto' }}>
          <defs>
            <mask id="vending-tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {spotlightRect && (
                <rect
                  x={spotlightRect.left} y={spotlightRect.top}
                  width={spotlightRect.width} height={spotlightRect.height}
                  rx="12" fill="black"
                />
              )}
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.65)" mask="url(#vending-tour-mask)" />
        </svg>

        {/* Spotlight ring */}
        {spotlightRect && (
          <div
            className="absolute border-2 border-sky-400 rounded-xl shadow-[0_0_0_4px_rgba(56,189,248,0.25)] transition-all duration-500 ease-out"
            style={{
              top: spotlightRect.top, left: spotlightRect.left,
              width: spotlightRect.width, height: spotlightRect.height,
              pointerEvents: 'none',
            }}
            data-testid="vending-tour-spotlight"
          />
        )}

        {/* Tooltip Card */}
        <div
          className="absolute z-[9999] bg-white rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden animate-in fade-in duration-200"
          style={{ ...getTooltipStyle(), pointerEvents: 'auto' }}
          data-testid="vending-tour-tooltip"
        >
          <div className="bg-gradient-to-r from-sky-500 to-cyan-500 px-5 py-3 flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-white text-[10px] font-semibold opacity-90 uppercase tracking-wide flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Vending Fleet Tour · Step {currentIdx + 1} of {steps.length}
              </p>
              <h3 className="text-white font-bold text-sm truncate" data-testid="vending-tour-title">{step.title}</h3>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={togglePauseAudio}
                className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors"
                title={audioPaused || !audioPlaying ? 'Play narration' : 'Pause narration'}
                data-testid="vending-tour-pause-btn"
                disabled={audioMuted}
              >
                {audioPaused || !audioPlaying ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </button>
              <button
                onClick={() => persistMuted(!audioMuted)}
                className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors"
                title={audioMuted ? 'Unmute' : 'Mute'}
                data-testid="vending-tour-mute-btn"
                aria-pressed={audioMuted}
              >
                {audioMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              <button onClick={dismiss} className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Skip tour" data-testid="vending-tour-dismiss">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="px-5 py-4">
            <p className="text-sm text-zinc-600 leading-relaxed" data-testid="vending-tour-narration">{step.narration}</p>
            {(audioPlaying || audioPaused) && !audioMuted && (
              <div className="flex items-center gap-2 mt-3 text-xs text-sky-600">
                {audioPaused ? (
                  <><Pause className="h-3 w-3" /> Narration paused — press play to resume</>
                ) : (
                  <><Volume2 className="h-3 w-3 animate-pulse" /> Playing narration…</>
                )}
              </div>
            )}
            {audioMuted && (
              <div className="flex items-center gap-2 mt-3 text-xs text-zinc-400">
                <VolumeX className="h-3 w-3" /> Muted — auto-advance is paused. Click Next to keep going.
              </div>
            )}
          </div>

          <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-1">
              {Array.from({ length: steps.length }, (_, i) => (
                <div key={i} className={cn(
                  'w-1.5 h-1.5 rounded-full transition-colors',
                  i === currentIdx ? 'bg-sky-500' : i < currentIdx ? 'bg-sky-300' : 'bg-zinc-300',
                )} />
              ))}
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={goPrev} disabled={currentIdx === 0} className="h-8 text-xs" data-testid="vending-tour-prev">
                <ChevronLeft className="h-3.5 w-3.5 mr-0.5" /> Back
              </Button>
              <Button size="sm" onClick={goNext} className="bg-sky-600 hover:bg-sky-700 h-8 text-xs" data-testid="vending-tour-next">
                {currentIdx === steps.length - 1 ? 'Finish' : 'Next'} <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
